import { zodResolver } from '@hookform/resolvers/zod'
import { STRUCTURE_TYPE, UNIT_TYPE } from '@idc-forms/schemas'
import {
  type V2CreateAdminPropertyInput,
  V2CreateAdminPropertySchema
} from '@idc-forms/schemas/api-v2/admin-properties'
import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useProjectsV2LookupQuery } from '@/features/projects/hooks/use-projects-v2-lookup-query'
import { useTypePropertiesV2LookupQuery } from '@/features/type-properties/hooks/use-type-properties-v2-lookup-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

const UNIT_TYPE_LABELS: Record<string, string> = {
  [UNIT_TYPE.APT]: 'Apartamento',
  [UNIT_TYPE.V]: 'Villa',
  [UNIT_TYPE.C]: 'Casa'
}

const STRUCTURE_TYPE_LABELS: Record<string, string> = {
  [STRUCTURE_TYPE.T]: 'Torre',
  [STRUCTURE_TYPE.V]: 'Villa'
}

/** IUSI en el wire v2 = Money no nulo (`{ amountMinor, currency }`). */
type IusiMoney = NonNullable<V2CreateAdminPropertyInput['iusiAmount']>

type PropertyV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. `project`/`typeProperty` son ids públicos. */
  defaultValues?: Partial<V2CreateAdminPropertyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: V2CreateAdminPropertyInput) => void
  onCancel: () => void
}

/**
 * Formulario de inmueble (crear/editar) para el side panel, contra la API v2.
 * Espeja el layout del form v1 (tres secciones: identificación, construcción,
 * datos adicionales) pero cambia el transporte de las relaciones: proyecto y
 * subtipo se alimentan de los lookups v2 (valores `prj_`/`typ_`) y el subtipo
 * cascadea por proyecto en el cliente. El IUSI se edita en quetzales pero viaja
 * como Money (GTQ). RHF + zodResolver con el MISMO schema v2 que valida el backend.
 */
export function PropertyV2Form({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: PropertyV2FormProps) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<V2CreateAdminPropertyInput>({
    resolver: zodResolver(V2CreateAdminPropertySchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const selectedProject = watch('project')
  const { data: projects = [] } = useProjectsV2LookupQuery()
  const { data: subtypes = [] } = useTypePropertiesV2LookupQuery()

  // Un nombre de proyecto duplicado muestra el código para desambiguar.
  const projectNameCounts = projects.reduce<Record<string, number>>((acc, project) => {
    acc[project.name] = (acc[project.name] ?? 0) + 1
    return acc
  }, {})

  // El subtipo cascadea por proyecto EN EL CLIENTE: el lookup trae todos con su `prj_`.
  const subtypeOptions = useMemo(
    () => subtypes.filter((subtype) => subtype.project === selectedProject),
    [subtypes, selectedProject]
  )

  const numberField = (value: number | undefined, onChange: (v: number | undefined) => void) => ({
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
    }
  })

  /** Enlaza un input de quetzales (mayor) al Money del wire (`amountMinor` + GTQ). */
  const iusiField = (
    value: IusiMoney | undefined,
    onChange: (v: IusiMoney | undefined) => void
  ) => ({
    value: value ? String(value.amountMinor / 100) : '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '' || Number.isNaN(e.target.valueAsNumber)) {
        onChange(undefined)
        return
      }
      onChange({ amountMinor: Math.round(e.target.valueAsNumber * 100), currency: 'GTQ' })
    }
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación del inmueble</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="project"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.project)}>
                <FieldLabel htmlFor="property-project">Proyecto</FieldLabel>
                <NativeSelect
                  id="property-project"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    // El subtipo depende del proyecto: al cambiarlo se invalida.
                    setValue('typeProperty', '' as V2CreateAdminPropertyInput['typeProperty'])
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un proyecto
                  </NativeSelectOption>
                  {projects.map((project) => (
                    <NativeSelectOption key={project.id} value={project.id}>
                      {projectNameCounts[project.name] > 1
                        ? `${project.name} (${project.code})`
                        : project.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.project]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="typeProperty"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.typeProperty)}>
                <FieldLabel htmlFor="property-type">Subtipo</FieldLabel>
                <NativeSelect
                  id="property-type"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                  disabled={!selectedProject}
                >
                  <NativeSelectOption value="" disabled>
                    {selectedProject ? 'Selecciona un subtipo' : 'Primero elige un proyecto'}
                  </NativeSelectOption>
                  {/* Varios subtipos comparten nombre (p.ej. "APARTAMENTO"): el CÓDIGO
                      es el identificador único, el nombre solo acompaña. */}
                  {subtypeOptions.map((subtype) => (
                    <NativeSelectOption key={subtype.id} value={subtype.id}>
                      {subtype.name && subtype.name !== subtype.code
                        ? `${subtype.code} — ${subtype.name}`
                        : subtype.code}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.typeProperty]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="property-name">Nombre</FieldLabel>
                <Input id="property-name" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="property-code">Código</FieldLabel>
                <Input id="property-code" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="unitType"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-unit-type">Tipo de unidad</FieldLabel>
                  <NativeSelect
                    id="property-unit-type"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value || undefined)
                    }}
                  >
                    <NativeSelectOption value="">Sin especificar</NativeSelectOption>
                    {Object.values(UNIT_TYPE).map((value) => (
                      <NativeSelectOption key={value} value={value}>
                        {UNIT_TYPE_LABELS[value]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="structureType"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-structure-type">Estructura</FieldLabel>
                  <NativeSelect
                    id="property-structure-type"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value || undefined)
                    }}
                  >
                    <NativeSelectOption value="">Sin especificar</NativeSelectOption>
                    {Object.values(STRUCTURE_TYPE).map((value) => (
                      <NativeSelectOption key={value} value={value}>
                        {STRUCTURE_TYPE_LABELS[value]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="structureNumber"
              render={({ field }) => (
                <Field data-invalid={Boolean(errors.structureNumber)}>
                  <FieldLabel htmlFor="property-structure-number">No. de estructura</FieldLabel>
                  <Input
                    id="property-structure-number"
                    type="number"
                    {...numberField(field.value, field.onChange)}
                  />
                  <FieldError errors={[errors.structureNumber]} />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="identifier"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-identifier">Identificador</FieldLabel>
                  <Input
                    id="property-identifier"
                    type="number"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
          </div>

          <Controller
            control={control}
            name="tipo"
            render={({ field }) => (
              <Field>
                <FieldLabel htmlFor="property-tipo">Tipo</FieldLabel>
                <Input id="property-tipo" value={field.value ?? ''} onChange={field.onChange} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Datos de construcción</FieldLegend>
        <FieldGroup className="gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="constructionM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-construction">Construcción (m²)</FieldLabel>
                  <Input
                    id="property-construction"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terrainM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrain-m2">Terreno (m²)</FieldLabel>
                  <Input
                    id="property-terrain-m2"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terrainV2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrain-v2">Terreno (v²)</FieldLabel>
                  <Input
                    id="property-terrain-v2"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terraceM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrace">Terraza (m²)</FieldLabel>
                  <Input
                    id="property-terrace"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="parkingM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-parking">Parqueo (m²)</FieldLabel>
                  <Input
                    id="property-parking"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
          </div>
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Datos adicionales</FieldLegend>
        <FieldGroup className="gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="property"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-finca">Finca</FieldLabel>
                  <Input id="property-finca" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="folio"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-folio">Folio</FieldLabel>
                  <Input id="property-folio" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="book"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-book">Libro</FieldLabel>
                  <Input id="property-book" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="iusiAmount"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-iusi">Monto IUSI (Q)</FieldLabel>
                  <Input
                    id="property-iusi"
                    type="number"
                    step="any"
                    inputMode="decimal"
                    {...iusiField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="waterMeter"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-water">Contador de agua</FieldLabel>
                  <Input id="property-water" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="electricityMeter"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-electricity">Contador de luz</FieldLabel>
                  <Input
                    id="property-electricity"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          </div>

          <Controller
            control={control}
            name="telephone"
            render={({ field }) => (
              <Field>
                <FieldLabel htmlFor="property-telephone">Teléfono</FieldLabel>
                <Input
                  id="property-telephone"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="property-active">Activo</FieldLabel>
                <Switch
                  id="property-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
